'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Interfaces, Finance } from 'doodle-icons';
import { ReadingDoodle, GroovyDoodle, MeditatingDoodle, SittingReadingDoodle } from 'react-open-doodles';
import { PERSONAS, type PersonaId } from '@/lib/personas';
import RoughAnnotation from '@/components/common/RoughAnnotation';
import { ScribbleCalculator } from '@/components/common/Scribbles';

/* ── Per-persona accent colors (reuse StudentBenefits pattern) ──── */
const PERSONA_COLORS: Record<PersonaId, {
  bg: string; iconBg: string; iconColor: string; tape: string;
}> = {
  teacher:        { bg: '#fff5cc', iconBg: 'rgba(230,168,23,0.14)', iconColor: '#d4960c', tape: 'rgba(230,168,23,0.22)' },
  'center-owner': { bg: '#dff0ff', iconBg: 'rgba(61,123,217,0.14)', iconColor: '#3d7bd9', tape: 'rgba(61,123,217,0.20)' },
  'school-leader':{ bg: '#e0f7ec', iconBg: 'rgba(50,151,87,0.12)', iconColor: '#329757', tape: 'rgba(50,151,87,0.18)' },
  'student-parent':{ bg: '#ffe4ec', iconBg: 'rgba(217,72,120,0.12)', iconColor: '#d94878', tape: 'rgba(217,72,120,0.18)' },
};

const STICKY_ROTATIONS = [-0.6, 0.5, -0.4, 0.7];

/* ── Per-persona character doodles (student's graphite drawing) ── */
const PERSONA_CHARACTERS: Record<PersonaId, React.FC<any>> = {
  teacher: ReadingDoodle,
  'center-owner': GroovyDoodle,
  'school-leader': MeditatingDoodle,
  'student-parent': SittingReadingDoodle,
};

/* ── Benefit data per persona ─────────────────────────────────── */
interface Benefit {
  title: string;
  body: string;
  category: string;
  icon: React.ReactNode;
  stat?: string;
}

const PERSONA_BENEFITS: Record<PersonaId, {
  headline: string;
  subheadline: string;
  benefits: Benefit[];
}> = {
  teacher: {
    headline: 'Grade less. Teach more.',
    subheadline: 'IntelGrader handles the red pen so you can focus on what matters — your students.',
    benefits: [
      { title: 'Instant grading', body: 'Upload a worksheet, get results in seconds. No more late nights marking papers.', category: 'Speed', stat: '10+ hrs/wk', icon: <Interfaces.Zap fill="currentColor" /> },
      { title: 'Formula-level feedback', body: 'AI traces the exact formula step where each student slipped \u2014 with concept-level explanations.', category: 'Precision', icon: <Interfaces.Target fill="currentColor" /> },
      { title: 'Concept mastery tracking', body: 'Track mastery across concept nodes over time. No spreadsheets, no guesswork.', category: 'Growth', icon: <Finance.TrendUp fill="currentColor" /> },
      { title: 'Curriculum graph', body: 'Upload your worksheets. IntelGrader maps them to concept nodes and grades to your rubric.', category: 'Flexibility', icon: <Interfaces.Folder fill="currentColor" /> },
    ],
  },
  'center-owner': {
    headline: 'Scale without hiring more tutors.',
    subheadline: 'Handle more students per tutor while improving feedback quality.',
    benefits: [
      { title: '3x tutor capacity', body: 'Each tutor handles triple the students when grading is automated.', category: 'Scale', stat: '3x', icon: <Interfaces.Zap fill="currentColor" /> },
      { title: 'Consistent quality', body: 'Every student gets the same standard of feedback, regardless of which tutor grades.', category: 'Quality', icon: <Interfaces.Shield fill="currentColor" /> },
      { title: 'Parent intelligence reports', body: 'Concept-mapped progress reports that show mastery, not just marks. Parents see what matters.', category: 'Trust', icon: <Interfaces.Doc fill="currentColor" /> },
      { title: 'Multi-branch visibility', body: 'One dashboard across all your locations.', category: 'Visibility', icon: <Interfaces.Analytics fill="currentColor" /> },
    ],
  },
  'school-leader': {
    headline: 'Data-driven teaching. Happier teachers.',
    subheadline: 'Give your teachers back their evenings while gaining school-wide insights.',
    benefits: [
      { title: 'Concept analytics', body: 'See concept mastery trends across all classes, subjects, and teachers.', category: 'Data', icon: <Interfaces.Analytics fill="currentColor" /> },
      { title: 'Reduce teacher burnout', body: 'Eliminating grading drudgery improves retention and morale.', category: 'Retention', icon: <Interfaces.Shield fill="currentColor" /> },
      { title: 'Parent intelligence reports', body: 'Concept-mapped reports that answer parent questions before they\u2019re asked.', category: 'Trust', icon: <Interfaces.Doc fill="currentColor" /> },
      { title: 'Seamless onboarding', body: 'Teachers start in minutes. No training needed — just upload and go.', category: 'Adoption', icon: <Interfaces.Zap fill="currentColor" /> },
    ],
  },
  'student-parent': {
    headline: 'Feedback that helps you learn.',
    subheadline: 'Students get clarity after every assignment. Parents see real progress.',
    benefits: [
      { title: 'Instant spatial feedback', body: 'See errors right on the page — not a week later. Know exactly which concept needs work.', category: 'Speed', icon: <Interfaces.Zap fill="currentColor" /> },
      { title: 'Clear next steps', body: 'Every graded paper comes with what to practice next.', category: 'Clarity', icon: <Interfaces.Target fill="currentColor" /> },
      { title: 'Concept mastery over time', body: 'Track mastery across concept nodes with visual dashboards.', category: 'Growth', icon: <Finance.TrendUp fill="currentColor" /> },
      { title: 'Parent intelligence reports', body: 'Concept-mapped reports that show exactly what your child is mastering.', category: 'Trust', icon: <Interfaces.Doc fill="currentColor" /> },
    ],
  },
};

/* ── Component ────────────────────────────────────────────────── */
const PersonaSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<PersonaId>('teacher');

  // Scroll reveal
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Listen for header persona-switch events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.tab) setActiveTab(detail.tab);
    };
    window.addEventListener('persona-switch', handler);
    return () => window.removeEventListener('persona-switch', handler);
  }, []);

  const data = PERSONA_BENEFITS[activeTab];
  const colors = PERSONA_COLORS[activeTab];
  const CharacterComponent = PERSONA_CHARACTERS[activeTab];

  return (
    <section id="personas" ref={sectionRef} className="persona-section section-base scroll-mt-32">
      <div className={`bg-benday-dots ${isVisible ? 'is-visible' : ''}`} />

      <div className="persona-section__inner">
        {/* Section header */}
        <div className={`section-header section-header--secondary section-header--persona reveal-fade-up reveal-stagger ${isVisible ? 'is-revealed' : ''}`} style={{ maxWidth: '58rem' }}>
          <span className="section-eyebrow">Who it&rsquo;s for</span>
          <h2 className="section-title">{data.headline}</h2>
          <p className="section-subtitle">{data.subheadline}</p>
        </div>

        {/* Tab bar */}
        <div className={`persona-tabs ${isVisible ? 'is-visible' : ''}`} role="tablist">
          {PERSONAS.map((p) => (
            <button
              key={p.id}
              role="tab"
              aria-selected={activeTab === p.id}
              className={`persona-tab ${activeTab === p.id ? 'persona-tab--active' : ''}`}
              onClick={() => setActiveTab(p.id)}
            >
              <span className="persona-tab__icon" aria-hidden="true">
                {React.createElement(p.icon, { width: 18, height: 18 })}
              </span>
              <span className="persona-tab__label">{p.shortLabel}</span>
            </button>
          ))}
        </div>

        {/* Tab content: sticky note cards */}
        <div className="persona-section__grid" key={activeTab}>
          {data.benefits.map((item, idx) => {
            const rotation = STICKY_ROTATIONS[idx % STICKY_ROTATIONS.length];
            return (
              <div
                key={`${activeTab}-${item.title}`}
                className={`sticky-note ${isVisible ? `sticky-note--animate sticky-note--delay-${idx}` : 'sticky-note--hidden'}`}
                style={{
                  '--sticky-bg': colors.bg,
                  '--sticky-icon-bg': colors.iconBg,
                  '--sticky-icon-color': colors.iconColor,
                  '--sticky-tape': colors.tape,
                  '--sticky-rotation': `${rotation}deg`,
                  '--sticky-padding-extra': '0px',
                } as React.CSSProperties}
              >
                <div className="sticky-note__tape" />
                <div className="sticky-note__body">
                  <div className="sticky-note__left">
                    <span className="sticky-note__icon">{item.icon}</span>
                    <span className="sticky-note__category">{item.category}</span>
                  </div>
                  <div className="sticky-note__content">
                    <h3>
                      {item.title}
                      {item.stat && (
                        <RoughAnnotation
                          type="circle"
                          color="#329757"
                          strokeWidth={1.5}
                          padding={4}
                          animationDuration={800}
                          triggerOnScroll={true}
                          scrollThreshold={0.3}
                        >
                          <span className="persona-section__stat">{item.stat}</span>
                        </RoughAnnotation>
                      )}
                    </h3>
                    <p>{item.body}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Calculator CTA banner */}
        <div className={`persona-section__calc-banner-wrap reveal-fade-up ${isVisible ? 'is-revealed' : ''}`}>
          <div className="persona-section__calc-banner">
            <span className="persona-section__calc-tape persona-section__calc-tape--left" aria-hidden="true" />
            <span className="persona-section__calc-tape persona-section__calc-tape--right" aria-hidden="true" />
            <ScribbleCalculator className="persona-section__calc-icon" aria-hidden="true" />
            <span className="persona-section__calc-text">
              Want to see your personal savings?
            </span>
            <Link href="/get-started" className="btn btn-primary btn-sm">
              Calculate your time saved &rarr;
            </Link>
          </div>
        </div>

        {/* Character illustration — student's graphite pencil doodle */}
        <div className="persona-section__character" aria-hidden="true" key={`char-${activeTab}`}>
          <CharacterComponent ink="#6b7a95" accent="transparent" />
        </div>
      </div>
    </section>
  );
};

export default PersonaSection;
